<template>
  <transition name="modal">
    <div
      v-if="show"
      class="modal-mask"
      :class="{ 'nested-modal-mask': nested }"
      @click.self="onBackdropClick"
    >
      <div class="modal-wrapper">
        <div
          class="modal-dialog modal-dialog-scrollable"
          :class="[dialogSizeClass, customClass]"
          role="document"
        >
          <div class="modal-content">
            <!-- Cabecera del Modal -->
            <div class="modal-header">
              <h5 class="modal-title">
                <slot name="title">{{ title }}</slot>
              </h5>
              <button
                type="button"
                class="close"
                aria-label="Cerrar"
                @click="onClose"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <!-- Cuerpo del Modal -->
            <div
              ref="modalBody"
              class="modal-body"
              @input="onFieldInput"
              @change="onFieldInput"
            >
              <!-- Alerta informativa opcional antes de la advertencia -->
              <slot name="toggle-info" v-if="isToggle"></slot>

              <!-- Alerta de advertencia para operaciones toggle (suspender / reactivar) -->
              <div
                v-if="isToggle && hasToggleWarning"
                class="alert alert-warning d-flex align-items-center mb-3"
                role="alert"
              >
                <i
                  class="bi bi-exclamation-triangle-fill mr-2"
                  style="font-size: 1.25rem;"
                ></i>
                <div>
                  <slot name="toggle-message">
                    {{ toggleMessage }}
                  </slot>
                </div>
              </div>

              <!-- Pregunta de confirmación fuera de la alerta para operaciones toggle -->
              <p v-if="isToggle" class="mb-0">
                <slot name="toggle-confirm">
                  ¿Está seguro de realizar esta acción?
                </slot>
              </p>

              <!-- Contenido principal / formulario -->
              <slot></slot>
            </div>

            <!-- Pie del Modal con botones de acción -->
            <div class="modal-footer" v-if="showFooter">
              <slot name="footer">
                <button
                  type="button"
                  class="btn btn-secondary"
                  @click="onClose"
                >
                  {{ cancelText }}
                </button>
                <button
                  v-if="showSave"
                  type="button"
                  class="btn"
                  :class="'btn-' + saveVariant"
                  :disabled="loading"
                  @click="onSaveClick"
                >
                  <span
                    v-if="loading"
                    class="spinner-border spinner-border-sm mr-1"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {{ saveText }}
                </button>
              </slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
export default {
  name: "CrudModal",

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "md",
      validator: (val) => ["sm", "md", "lg", "xl"].includes(val),
    },
    customClass: {
      type: String,
      default: "",
    },
    nested: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    showSave: {
      type: Boolean,
      default: true,
    },
    saveText: {
      type: String,
      default: "Guardar",
    },
    cancelText: {
      type: String,
      default: "Cancelar",
    },
    saveVariant: {
      type: String,
      default: "primary",
    },
    isToggle: {
      type: Boolean,
      default: false,
    },
    toggleMessage: {
      type: String,
      default: "",
    },
    closeOnBackdrop: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    dialogSizeClass() {
      if (this.size === "sm") return "modal-sm";
      if (this.size === "lg") return "modal-lg";
      if (this.size === "xl") return "modal-xl";
      return "";
    },
    hasToggleWarning() {
      if (this.toggleMessage && this.toggleMessage.trim().length > 0) {
        return this.toggleMessage.trim() !== "¿Está seguro de realizar esta acción?";
      }
      const slotFn = this.$scopedSlots && this.$scopedSlots["toggle-message"];
      if (slotFn) {
        const vnodes = slotFn();
        if (!vnodes || !vnodes.length) return false;
        return vnodes.some((vn) => {
          if (vn.tag) return true;
          const text = vn.text ? vn.text.trim() : "";
          return text.length > 0 && text !== "¿Está seguro de realizar esta acción?";
        });
      }
      const vnodes = this.$slots && this.$slots["toggle-message"];
      if (vnodes && vnodes.length) {
        return vnodes.some((vn) => {
          if (vn.tag) return true;
          const text = vn.text ? vn.text.trim() : "";
          return text.length > 0 && text !== "¿Está seguro de realizar esta acción?";
        });
      }
      return false;
    },
  },

  data() {
    return {
      observer: null,
      lastErrorText: "",
    };
  },

  watch: {
    show(val) {
      if (val) {
        this.lastErrorText = "";
        this.$nextTick(() => {
          this.setupObserver();
          this.syncErrorState();
        });
      } else {
        this.teardownObserver();
        this.lastErrorText = "";
      }
    },
  },

  mounted() {
    if (this.show) {
      this.$nextTick(() => {
        this.setupObserver();
        this.syncErrorState();
      });
    }
  },

  beforeDestroy() {
    this.teardownObserver();
  },

  methods: {
    onSaveClick() {
      this.lastErrorText = "";
      this.$emit("save");
      this.$nextTick(() => {
        this.syncErrorState();
      });
    },
    onClose() {
      this.$emit("close");
    },
    onBackdropClick() {
      if (this.closeOnBackdrop) {
        this.onClose();
      }
    },
    onFieldInput(e) {
      const target = e && e.target;
      if (target && target.classList && target.classList.contains("crud-auto-invalid")) {
        target.classList.remove("crud-auto-invalid", "is-invalid");
      }
    },
    setupObserver() {
      this.teardownObserver();
      const body = this.$refs.modalBody;
      if (!body || typeof MutationObserver === "undefined") return;
      this.observer = new MutationObserver(() => {
        this.syncErrorState();
      });
      this.observer.observe(body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    },
    teardownObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    extractErrorFields(errorText) {
      const fields = new Set();
      const aliases = {
        precio_sin_imp: ["precio_sin_imp", "full_price"],
        full_price: ["full_price", "precio_sin_imp"],
        precio_con_imp: ["precio_con_imp", "full_price_tax"],
        full_price_tax: ["full_price_tax", "precio_con_imp"],
        precio_min: ["precio_min", "min_price_bs"],
        min_price_bs: ["min_price_bs", "precio_min"],
        numero_corto: ["numero_corto", "numero"],
        numero: ["numero", "numero_corto"],
        ctr_url: ["ctr_url", "endpoint_eventos", "endpoint"],
        endpoint_eventos: ["ctr_url", "endpoint_eventos", "endpoint"],
      };

      const addField = (f) => {
        if (!f) return;
        const clean = f.trim();
        fields.add(clean);
        if (aliases[clean]) {
          aliases[clean].forEach((a) => fields.add(a));
        }
      };

      const colRegex = /Columna\s+['"]([^'"]+)['"]/gi;
      let match;
      while ((match = colRegex.exec(errorText)) !== null) {
        addField(match[1]);
      }

      const labelToField = {
        integrador: "id_integrador",
        "nombre del integrador": "integrador",
        secuencia: "sequence",
        "descripción": "descripcion",
        descripcion: "descripcion",
        "precio sin impuestos": "full_price",
        "precio (con impuestos)": "full_price_tax",
        "precio con impuestos": "full_price_tax",
        "precio mínimo": "min_price_bs",
        "precio minimo": "min_price_bs",
        lifetime: "lifetime",
        frecuencia: "frecuency",
        restricciones: "bill_restriction",
        "código tas": "cod_tas",
        "codigo tas": "cod_tas",
        "número corto": "numero_corto",
        "numero corto": "numero_corto",
        keyword: "keyword",
        producto: "producto",
        "nombre (alias)": "alias",
        alias: "alias",
        "categoría": "id_categoria",
        categoria: "id_categoria",
        operador: "id_operador",
        "tipo de suscripción": "id_tipo_suscripcion",
        "tipo de suscripcion": "id_tipo_suscripcion",
        "endpoint para ctrs": "ctr_url",
        "eventos de ctrs": "ctr_events",
        "nombre de usuario": "name",
        usuario: "name",
        nombre: "nombre",
        "correo electrónico": "email",
        "correo electronico": "email",
        "contraseña": "password",
        contrasena: "password",
        rol: "id_rol",
        valor: "valor",
        atributo: "id_atributo",
        estado: "activo",
      };

      const quotedLabelRegex = /El campo\s+["']([^"']+)["']/gi;
      while ((match = quotedLabelRegex.exec(errorText)) !== null) {
        const key = match[1].trim().toLowerCase();
        if (labelToField[key]) {
          addField(labelToField[key]);
          if (key === "keyword") addField("id_keyword");
          if (key === "producto") addField("id_producto");
          if (key === "operador") addField("operador");
        } else {
          addField(key);
        }
      }

      const lower = errorText.toLowerCase();
      Object.keys(labelToField).forEach((lbl) => {
        if (lower.includes(`${lbl}:`)) {
          addField(labelToField[lbl]);
          if (lbl === "keyword") addField("id_keyword");
          if (lbl === "producto") addField("id_producto");
        }
      });

      if (/seleccionar un integrador|seleccione un integrador/.test(lower)) {
        addField("id_integrador");
      }
      if (/seleccionar una categor[ií]a/.test(lower)) {
        addField("id_categoria");
      }
      if (/nombre del integrador/.test(lower) || (/ese integrador\b/.test(lower) && !lower.includes(" y "))) {
        addField("integrador");
      }
      if (/la keyword|y keyword\b|ese keyword\b|campo "keyword"/.test(lower)) {
        addField("keyword");
        addField("id_keyword");
      }
      if (/cod\s*tas|c[oó]digo\s*tas/.test(lower)) {
        addField("cod_tas");
      }
      if (/numero\s*corto|n[uú]mero\s*corto/.test(lower)) {
        addField("numero_corto");
      }
      if (/secuencia:|\bsequence\b|formato de secuencia|campo "secuencia"/.test(lower)) {
        addField("sequence");
      }
      if (/precio \(con impuestos\)/.test(lower)) {
        addField("full_price_tax");
      }
      if (/precio sin impuestos/.test(lower)) {
        addField("full_price");
      }
      if (/precio m[ií]nimo/.test(lower)) {
        addField("min_price_bs");
      }
      if (/restricciones/.test(lower)) {
        addField("bill_restriction");
      }
      if (/frecuencia/.test(lower)) {
        addField("frecuency");
      }
      if (/lifetime/.test(lower)) {
        addField("lifetime");
      }
      if (/descripci[oó]n/.test(lower)) {
        addField("descripcion");
      }
      if (/y producto\b|ese producto\b|campo "producto"/.test(lower)) {
        addField("producto");
        addField("id_producto");
      }
      return Array.from(fields);
    },
    syncErrorState() {
      const body = this.$refs.modalBody;
      if (!body) return;

      const alertEl = body.querySelector(".alert-danger");
      const prevAutoInvalid = body.querySelectorAll(".crud-auto-invalid");
      prevAutoInvalid.forEach((el) => {
        el.classList.remove("crud-auto-invalid", "is-invalid");
      });

      if (!alertEl) {
        this.lastErrorText = "";
        return;
      }

      const errorText = (alertEl.textContent || "").trim();
      if (!errorText) {
        this.lastErrorText = "";
        return;
      }

      const matchedFields = this.extractErrorFields(errorText);
      matchedFields.forEach((fieldName) => {
        const targets = body.querySelectorAll(
          `[name="${fieldName}"], #${fieldName}, #${fieldName}_select`
        );
        targets.forEach((el) => {
          if (!el.disabled && !el.readOnly) {
            el.classList.add("is-invalid", "crud-auto-invalid");
          }
        });
      });

      if (body.querySelectorAll(".is-invalid").length === 0) {
        const editableFields = Array.from(
          body.querySelectorAll("input.form-control, select.form-control, textarea.form-control")
        ).filter((el) => !el.disabled && !el.readOnly && el.type !== "hidden");
        if (editableFields.length === 1) {
          editableFields[0].classList.add("is-invalid", "crud-auto-invalid");
        } else if (/campos requeridos/i.test(errorText)) {
          editableFields.forEach((el) => {
            if (!el.value || el.value === "null") {
              el.classList.add("is-invalid", "crud-auto-invalid");
            }
          });
        }
      }

      if (errorText !== this.lastErrorText) {
        this.lastErrorText = errorText;
        this.$nextTick(() => {
          const firstInvalid = body.querySelector(".is-invalid");
          if (firstInvalid && typeof firstInvalid.scrollIntoView === "function") {
            firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
            if (typeof firstInvalid.focus === "function") {
              try {
                firstInvalid.focus({ preventScroll: true });
              } catch (e) {
                // ignore focus errors
              }
            }
          } else if (typeof alertEl.scrollIntoView === "function") {
            alertEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter,
.modal-leave-to {
  opacity: 0;
}

.modal-body ::v-deep .alert-danger {
  position: sticky;
  bottom: 0;
  z-index: 20;
  background-color: #f8d7da;
  border-color: #f5c6cb;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.12);
  margin-bottom: 0 !important;
}
</style>
